import React, { useMemo, useRef, useEffect } from 'react';

import CardFileCarousel from './cardFileCarousel/cardFileCarousel';
import * as S from './styles';
import KOGIconCloseModal from '@/main/components/shared/kogIconCloseModal/kogIconCloseModal';
import KOGCarouselCard from '@/main/components/shared/kogCarouselCard/kogCarouselCard';
import { GridFileResponse } from '@/main/components/shared/fileGrid/cardFileGrid';

interface IModalCarouselFileProps<Response extends { guid: string }> {
  dataGrid: Response[];
  open: boolean;
  currentIndexCarousel?: number;
  totalRecords: number;
  isLoading: boolean;
  getNextPageDataSearch: () => Promise<void> | void;
  setCurrentIndexCarousel?: React.Dispatch<React.SetStateAction<number>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mapToGridFile: (item: Response) => GridFileResponse;
  canDownload?: boolean;
  handleDownload?: (imageUrl: string, imageRecord: Response) => Promise<void>;
}

function ModalCarouselFile<Response extends { guid: string }>({
  dataGrid,
  open,
  currentIndexCarousel = 0,
  totalRecords,
  isLoading,
  getNextPageDataSearch,
  setCurrentIndexCarousel,
  setOpen,
  mapToGridFile,
  canDownload,
  handleDownload
}: Readonly<IModalCarouselFileProps<Response>>) {
  const handleClose = () => setOpen(false);

  const loadedCardsRef = useRef<Set<number>>(new Set());
  const previousDataGridLengthRef = useRef<number>(0);

  useEffect(() => {
    if (!open) {
      loadedCardsRef.current.clear();
      previousDataGridLengthRef.current = 0;
    }
  }, [open]);

  // Notation: Clear cache if the current index is too far from the cached indices
  useEffect(() => {
    const cacheIndices = Array.from(loadedCardsRef.current);
    const hasCurrentInCache = cacheIndices.includes(currentIndexCarousel);

    if (!hasCurrentInCache && cacheIndices.length > 0) {
      const minCacheIndex = Math.min(...cacheIndices);
      const maxCacheIndex = Math.max(...cacheIndices);
      const distanceFromCache = Math.min(
        Math.abs(currentIndexCarousel - minCacheIndex),
        Math.abs(currentIndexCarousel - maxCacheIndex)
      );

      if (distanceFromCache > 5) {
        loadedCardsRef.current.clear();
      }
    }
  }, [currentIndexCarousel]);

  useEffect(() => {
    if (dataGrid.length < previousDataGridLengthRef.current) {
      loadedCardsRef.current.clear();
    }
    previousDataGridLengthRef.current = dataGrid.length;
  }, [dataGrid.length]);

  const visibleCards = useMemo(() => {
    if (dataGrid.length === 0) return [];

    const cards: React.ReactElement[] = [];
    const bufferSize = 2;
    const startIndex = Math.max(0, currentIndexCarousel - bufferSize);
    const endIndex = Math.min(
      dataGrid.length - 1,
      currentIndexCarousel + bufferSize
    );

    if (currentIndexCarousel >= 0 && currentIndexCarousel < dataGrid.length) {
      loadedCardsRef.current.add(currentIndexCarousel);
    }

    for (let i = startIndex; i <= endIndex; i++) {
      if (i >= 0 && i < dataGrid.length) {
        loadedCardsRef.current.add(i);
      }
    }

    for (let i = 0; i < dataGrid.length; i++) {
      if (loadedCardsRef.current.has(i) && dataGrid[i]) {
        cards.push(
          <CardFileCarousel
            key={dataGrid[i].guid}
            file={mapToGridFile(dataGrid[i])}
            canDownload={canDownload}
            handleDownload={handleDownload}
            fileRecord={dataGrid[i]}
          />
        );
      } else {
        cards.push(
          <div
            key={`placeholder-${i}`}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              minWidth: '100%',
              height: '100%',
              background: 'transparent',
              padding: '1rem 0.8rem',
              borderRadius: '8px',
              textAlign: 'center',
              overflow: 'auto'
            }}></div>
        );
      }
    }

    return cards;
  }, [dataGrid, currentIndexCarousel]);

  return (
    <S.StyledModal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <S.Container>
        <KOGIconCloseModal handleClose={handleClose} />
        <S.AreaCarousel>
          <KOGCarouselCard
            isAbsoluteControls
            backToFirstPage
            backToLastPage
            totalRecords={totalRecords}
            cardsLenght={dataGrid.length}
            currentIndex={currentIndexCarousel}
            isLoading={isLoading}
            onClickNextPage={getNextPageDataSearch}
            setCurrentIndex={setCurrentIndexCarousel}>
            {visibleCards}
          </KOGCarouselCard>
        </S.AreaCarousel>
      </S.Container>
    </S.StyledModal>
  );
}

export default ModalCarouselFile;
